import { useState } from 'react';
import { hashKey, useQuery, type DefaultError, type QueryKey, type UseQueryOptions } from '@tanstack/react-query';

/** Keep the initial failure mounted while a user retries. Query/cache behavior
 * remains owned by TanStack; only the loading/error presentation is retained. */
export function useRecoveryQuery<TQueryFnData = unknown, TError = DefaultError,
  TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(
  options: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
) {
  const query = useQuery(options);
  const key = hashKey(options.queryKey);
  const [failure, setFailure] = useState<{key: string; error: TError} | null>(null);
  const retained = failure?.key === key && query.isPending ? failure : null;
  if (query.isError && (failure?.key !== key || failure.error !== query.error)) {
    setFailure({key, error: query.error});
  } else if (failure && (failure.key !== key || query.isSuccess)) {
    setFailure(null);
  }
  return {
    ...query,
    isLoading: query.isLoading && !retained,
    isError: query.isError || !!retained,
    error: retained?.error ?? query.error,
  };
}
